/* D-only media ownership. Every managed video opts out of the shared loader. */
(function () {
  'use strict';
  let libraryPromise;

  function loadHls(root) {
    if (window.Hls) return Promise.resolve(window.Hls);
    if (libraryPromise) return libraryPromise;
    libraryPromise = new Promise(function (resolve, reject) {
      const source = root.getAttribute('data-hls-library');
      if (!source) { reject(new Error('No HLS library configured')); return; }
      const script = document.createElement('script');
      const timer = window.setTimeout(function () { reject(new Error('HLS library timed out')); }, 4000);
      script.src = source;
      script.async = true;
      script.addEventListener('load', function () {
        window.clearTimeout(timer);
        if (window.Hls) resolve(window.Hls);
        else reject(new Error('HLS library unavailable'));
      });
      script.addEventListener('error', function () { window.clearTimeout(timer); reject(new Error('HLS library failed')); });
      document.head.appendChild(script);
    });
    return libraryPromise;
  }

  function init(root) {
    if (root.dataset.huhMediaReady) return;
    root.dataset.huhMediaReady = 'true';
    const reduced = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const connection = window.navigator && window.navigator.connection;
    let pageAway = false;
    const states = [];

    function shown(state) { return state.video.isConnected && !state.video.closest('[hidden]') && !document.hidden && !pageAway; }
    function automatic(state) { return state.auto && state.video.muted && !(reduced && reduced.matches) && !(connection && connection.saveData) && !state.userPaused && !state.blocked; }
    function wantsPlayback(state) { return shown(state) && state.inView && !state.suspended && (state.explicit || automatic(state)); }
    function clearDeadline(state) { window.clearTimeout(state.deadline); state.deadline = null; }
    function updateButtons(state, loading) {
      const playing = !state.video.paused && !state.video.ended;
      if (state.toggle) {
        state.toggle.setAttribute('aria-label', playing ? 'Pause background video' : 'Play background video');
        state.toggle.setAttribute('aria-pressed', String(playing));
        state.toggle.dataset.playing = String(playing);
        state.toggle.textContent = playing ? 'Pause video' : 'Play video';
      }
      if (state.overlay) {
        state.overlay.hidden = playing;
        state.overlay.setAttribute('aria-label', loading ? 'Loading video…' : state.overlayLabel);
      }
    }
    function startBuffer(state) {
      if (state.hls && state.ready && !state.buffering) { state.hls.startLoad(); state.buffering = true; }
    }
    function stopBuffer(state) {
      if (state.hls) state.hls.stopLoad();
      state.buffering = false;
    }
    function suspend(state) {
      state.suspended = true;
      state.explicit = false;
      state.pendingPlay = false;
      if (!state.video.paused) state.video.pause();
      stopBuffer(state);
      updateButtons(state);
    }
    function tryPlay(state) {
      if (!wantsPlayback(state) || !state.ready) return;
      startBuffer(state);
      if (state.pendingPlay || !state.video.paused) return;
      state.video.preload = 'auto';
      state.pendingPlay = true;
      updateButtons(state, true);
      let result;
      try { result = state.video.play(); } catch (error) { result = Promise.reject(error); }
      Promise.resolve(result).then(function () { state.pendingPlay = false; }, function () {
        state.pendingPlay = false;
        if (wantsPlayback(state)) { state.blocked = true; state.explicit = false; }
        stopBuffer(state);
        clearDeadline(state);
        updateButtons(state);
      });
    }
    function attachDirect(state, source, mode) {
      state.mode = mode;
      state.ready = true;
      state.switching = true;
      state.video.preload = wantsPlayback(state) ? 'auto' : 'metadata';
      state.video.src = source;
      state.video.load();
      tryPlay(state);
    }
    function useMp4(state) {
      if (state.mode === 'mp4') return;
      clearDeadline(state);
      state.fallback = true;
      if (state.hls) { state.hls.destroy(); state.hls = null; }
      state.buffering = false;
      state.pendingPlay = false;
      if (state.mp4) attachDirect(state, state.mp4, 'mp4');
      else { state.mode = 'failed'; state.ready = false; updateButtons(state); }
    }
    function prepare(state) {
      if (state.mode || !shown(state)) return;
      state.video.preload = wantsPlayback(state) ? 'auto' : 'metadata';
      if (!state.hlsSource) { useMp4(state); return; }
      if (state.hero && wantsPlayback(state)) state.deadline = window.setTimeout(function () {
        if (state.video.paused && state.mode !== 'mp4') useMp4(state);
      }, 4000);
      if (state.video.canPlayType('application/vnd.apple.mpegurl')) {
        attachDirect(state, state.hlsSource, 'native-hls');
        return;
      }
      state.mode = 'library';
      loadHls(root).then(function (Hls) {
        if (state.fallback || state.mode !== 'library') return;
        if (!Hls.isSupported()) { useMp4(state); return; }
        const hls = new Hls({
          autoStartLoad: false, capLevelToPlayerSize: false,
          maxBufferLength: 12, maxMaxBufferLength: 12, backBufferLength: 6,
          maxBufferSize: 12 * 1024 * 1024
        });
        state.hls = hls;
        state.mode = 'hls';
        state.switching = true;
        hls.on(Hls.Events.MEDIA_ATTACHED, function () { if (state.hls === hls) hls.loadSource(state.hlsSource); });
        hls.on(Hls.Events.MANIFEST_PARSED, function () {
          if (state.hls !== hls) return;
          state.ready = true;
          tryPlay(state);
          if (!wantsPlayback(state)) stopBuffer(state);
        });
        hls.on(Hls.Events.ERROR, function (_, data) { if (state.hls === hls && data && data.fatal) useMp4(state); });
        hls.attachMedia(state.video);
      }).catch(function () { if (!state.fallback) useMp4(state); });
    }
    function sync(state) {
      if (!shown(state) || !state.inView) suspend(state);
      else state.suspended = false;
      if (state.near && shown(state)) prepare(state);
      tryPlay(state);
    }
    function measure(state) {
      const rect = state.video.getBoundingClientRect();
      const rendered = rect.width > 0 && rect.height > 0 && !state.video.closest('[hidden]');
      state.near = rendered && rect.bottom > -200 && rect.top < window.innerHeight + 200;
      state.inView = rendered && rect.bottom > 0 && rect.top < window.innerHeight;
      sync(state);
    }
    function syncAll() { states.forEach(measure); }
    function requestPlay(state) {
      if (!shown(state)) return;
      state.inView = true;
      state.near = true;
      state.suspended = false;
      state.userPaused = false;
      state.blocked = false;
      state.explicit = true;
      updateButtons(state, true);
      if (state.failed && state.mode === 'mp4') { state.failed = false; state.video.load(); }
      prepare(state);
      tryPlay(state);
    }

    root.querySelectorAll('[data-huh-video]').forEach(function (video) {
      const hero = video.hasAttribute('data-huh-hero-video');
      const overlay = video.parentElement && video.parentElement.querySelector('[data-huh-play]');
      const state = {
        video: video, hero: hero, auto: video.hasAttribute('data-huh-autoplay'),
        mp4: video.getAttribute('data-src'), hlsSource: video.getAttribute('data-hls'),
        toggle: hero ? root.querySelector('[data-huh-media-toggle]') : null,
        overlay: overlay, overlayLabel: overlay ? overlay.getAttribute('aria-label') || 'Play video' : '',
        near: false, inView: false, mode: '', ready: false, userPaused: false,
        explicit: false, suspended: false, blocked: false, pendingPlay: false
      };
      states.push(state);
      video.setAttribute('data-av-skip', '');
      video.autoplay = false;
      video.removeAttribute('autoplay');
      video.controls = !hero;
      video.addEventListener('play', function () {
        if (!shown(state) || !state.inView) { suspend(state); return; }
        state.userPaused = false;
        state.blocked = false;
        if (!state.pendingPlay) state.explicit = true;
        startBuffer(state);
        updateButtons(state);
      });
      video.addEventListener('playing', function () {
        state.switching = false;
        state.pendingPlay = false;
        clearDeadline(state);
        if (!shown(state) || !state.inView) suspend(state);
        else updateButtons(state);
      });
      video.addEventListener('loadedmetadata', function () { state.switching = false; });
      video.addEventListener('pause', function () {
        if (!video.paused) return;
        if (!state.suspended && !state.switching) { state.userPaused = true; state.explicit = false; }
        stopBuffer(state);
        updateButtons(state);
      });
      video.addEventListener('ended', function () { state.explicit = false; updateButtons(state); });
      video.addEventListener('error', function () {
        if (state.mode !== 'mp4' && state.mode !== 'failed') useMp4(state);
        else { state.failed = true; state.blocked = true; state.explicit = false; clearDeadline(state); updateButtons(state); }
      });
      if (state.toggle) state.toggle.addEventListener('click', function () {
        if (video.paused) requestPlay(state);
        else {
          state.userPaused = true;
          state.explicit = false;
          state.pendingPlay = false;
          video.pause();
          stopBuffer(state);
          updateButtons(state);
        }
      });
      if (overlay) overlay.addEventListener('click', function () { requestPlay(state); });
      if (!hero) {
        const prepareNativeControl = function () {
          if (!shown(state)) return;
          state.inView = true;
          state.near = true;
          state.suspended = false;
          // An uninitialized native control needs a source in this same gesture.
          // Safari can use native HLS immediately; other browsers use the stable MP4.
          if (!state.mode || state.mode === 'library') {
            if (state.hlsSource && video.canPlayType('application/vnd.apple.mpegurl')) prepare(state);
            else useMp4(state);
          }
        };
        video.addEventListener('pointerdown', prepareNativeControl);
        video.addEventListener('click', prepareNativeControl);
        video.addEventListener('keydown', function (event) { if (event.key === ' ' || event.key === 'Enter') prepareNativeControl(); });
      }
      updateButtons(state);
    });

    if (window.IntersectionObserver) {
      const byVideo = new Map(states.map(function (state) { return [state.video, state]; }));
      const nearObserver = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { const state = byVideo.get(entry.target); state.near = entry.isIntersecting; sync(state); });
      }, { rootMargin: '200px', threshold: 0 });
      const viewObserver = new window.IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { const state = byVideo.get(entry.target); state.inView = entry.isIntersecting; sync(state); });
      }, { rootMargin: '0px', threshold: 0 });
      states.forEach(function (state) { nearObserver.observe(state.video); viewObserver.observe(state.video); });
      // The hero must not wait for an observer tick, the library, or DOMContentLoaded.
      states.filter(function (state) { return state.hero; }).forEach(measure);
    } else {
      window.addEventListener('scroll', syncAll, { passive: true });
      syncAll();
    }
    window.addEventListener('resize', syncAll);
    document.addEventListener('visibilitychange', syncAll);
    window.addEventListener('pagehide', function () { pageAway = true; states.forEach(suspend); });
    window.addEventListener('pageshow', function () { pageAway = false; syncAll(); });
    if (reduced && reduced.addEventListener) reduced.addEventListener('change', function () {
      states.forEach(function (state) { if (reduced.matches && state.auto && !state.explicit) suspend(state); });
      syncAll();
    });
  }

  document.querySelectorAll('.huh-complete-d').forEach(init);
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('.huh-complete-d').forEach(init);
  });
}());
